// ===========================================
// HOOK: useAuditor - Auditor con contexto automático (CORE)
// ===========================================

import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useSupabase } from '@/lib/supabase-context'
import { auditoriaService, crearAuditor } from '../services/auditoriaService'
import { useWorkspace } from '../providers/WorkspaceProvider'
import { useOrganizacion } from '../providers/OrganizacionProvider'
import type { AccionAuditoria, EntidadAuditoria } from '../types/auditoria'

/**
 * Hook para registrar acciones en el log de auditoría
 * Obtiene automáticamente el contexto de usuario, workspace y organización
 */
export function useAuditor() {
  const { user } = useAuth0()
  const location = useLocation()
  const { supabase } = useSupabase()
  const userEmail = user?.email || 'sistema@contadoresnet.com'

  // Obtener contexto de workspace
  const workspaceCtx = useWorkspace()
  const workspaceId = workspaceCtx.workspaceActual?.id

  // Obtener contexto de organización
  const orgCtx = useOrganizacion()
  const organizacionId = orgCtx.organizacionId ?? undefined

  // Contexto automático para todas las acciones
  const contexto = useMemo(
    () => ({
      workspace: workspaceId,
      pantalla: location.pathname,
      organizacion_id: organizacionId,
    }),
    [workspaceId, location.pathname, organizacionId]
  )

  const log = useCallback(
    async (
      accion: AccionAuditoria,
      entidad: EntidadAuditoria,
      entidadId?: string,
      detalle?: Record<string, unknown>
    ) => {
      if (!supabase) {
        console.warn('Supabase no disponible para auditoría')
        return
      }
      try {
        await auditoriaService.log(supabase, userEmail, accion, entidad, entidadId, detalle, contexto)
      } catch (error) {
        console.error('Error al registrar auditoría:', error)
      }
    },
    [supabase, userEmail, contexto]
  )

  const auditor = useCallback(
    () => (supabase ? crearAuditor(supabase, userEmail, contexto) : null),
    [supabase, userEmail, contexto]
  )

  return {
    log,
    auditor,
    userEmail,
    contexto,

    // Helpers de acceso rápido
    crear: useCallback(
      (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
        log('crear', entidad, entidadId, detalle),
      [log]
    ),
    editar: useCallback(
      (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
        log('editar', entidad, entidadId, detalle),
      [log]
    ),
    eliminar: useCallback(
      (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
        log('eliminar', entidad, entidadId, detalle),
      [log]
    ),
    cambiarEstado: useCallback(
      (
        entidad: EntidadAuditoria,
        entidadId: string,
        estadoAnterior: string,
        estadoNuevo: string
      ) =>
        log('cambiar_estado', entidad, entidadId, {
          estado_anterior: estadoAnterior,
          estado_nuevo: estadoNuevo,
        }),
      [log]
    ),
    subir: useCallback(
      (entidadId: string, detalle?: Record<string, unknown>) =>
        log('subir', 'documento', entidadId, detalle),
      [log]
    ),
    exportar: useCallback(
      (entidad: EntidadAuditoria, detalle?: Record<string, unknown>) =>
        log('exportar', entidad, undefined, detalle),
      [log]
    ),
    generarLote: useCallback(
      (detalle?: Record<string, unknown>) =>
        log('generar_lote', 'movimiento', undefined, detalle),
      [log]
    ),
    vincular: useCallback(
      (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
        log('vincular', entidad, entidadId, detalle),
      [log]
    ),
    desvincular: useCallback(
      (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
        log('desvincular', entidad, entidadId, detalle),
      [log]
    ),
  }
}

// Re-export del crearAuditor para uso directo
export { crearAuditor } from '../services/auditoriaService'
